import { createGlobalStyle, css } from 'styled-components'
import { AppTheme } from '@app/theme'
import DysonFutureBook from '@app/assets/fonts/DysonFutura-Book.woff'
import DysonFutureLight from '@app/assets/fonts/DysonFutura-Light.woff'
import DysonFutureMedium from '@app/assets/fonts/DysonFutura-Medium.woff'
import { transparentize } from 'polished'

const GlobalStyle = createGlobalStyle<{ theme: AppTheme; isBackoffice: boolean }>`
  ${({ theme, isBackoffice }) => css`
    ${isBackoffice &&
    css`
      @import url('https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;700&display=swap');
    `}

    @font-face {
      font-family: 'DysonFutura';
      src: url(${DysonFutureLight}) format('woff');
      font-weight: 300;
      font-style: normal;
    }

    @font-face {
      font-family: 'DysonFutura';
      src: url(${DysonFutureBook}) format('woff');
      font-weight: 400;
      font-style: normal;
    }

    @font-face {
      font-family: 'DysonFutura';
      src: url(${DysonFutureMedium}) format('woff');
      font-weight: 500;
      font-style: normal;
    }

    * {
      box-sizing: border-box;
      font-family: ${theme.font_family}, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
        'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    }

    html {
      font-size: 62.5%;
    }

    body {
      margin: 0;
      padding: 0;
      font-size: 1.6rem;
      font-weight: 400;
      background-color: ${theme.body_color};
      color: ${theme.font_color};
    }

    html,
    body {
      height: 100%;
    }

    h1 {
      font-size: 3.6rem;
      font-weight: 300;
      line-height: 1.18182;

      ${theme.media('sm')} {
        font-size: 4.4rem;
      }
    }

    h2 {
      font-size: 2.8rem;
      font-weight: 300;
      line-height: 1.28571;

      ${theme.media('sm')} {
        font-size: 3.6rem;
      }
    }

    h3 {
      font-size: 2.4rem;
      font-weight: 300;
      line-height: 1.28571;

      ${theme.media('sm')} {
        font-size: 2.8rem;
      }
    }

    h4 {
      font-size: 2rem;
      font-weight: 300;
      line-height: 1.28571;

      ${theme.media('sm')} {
        font-size: 2.4rem;
      }
    }

    p {
      line-height: 1.5;
    }

    a {
      cursor: pointer;
      color: ${theme.palette.primary[900]};
      text-underline-position: under;
      text-decoration-color: ${transparentize(0.5, theme.palette.primary[900])};
      text-decoration-thickness: from-font;
    }

    button {
      cursor: pointer;
    }

    ol {
      list-style: none;
      counter-reset: ol-counter;
      position: relative;
      margin: 0;
      padding-left: ${theme.spacing(4)};

      ${theme.media('sm')} {
        padding-left: ${theme.spacing(6)};
      }
    }

    ol > li {
      counter-increment: ol-counter;
      width: 100%;
      margin-bottom: 1em;
    }

    ol > li::before {
      content: counter(ol-counter);
      position: absolute;
      left: 0;
      color: #000000;
      border: 0.1rem solid #000000;
      text-align: center;
      border-radius: 2rem;
      width: 2rem;
      height: 2rem;
      line-height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      flex-shrink: 0;

      ${theme.media('sm')} {
        border-radius: 2.8rem;
        width: 2.8rem;
        height: 2.8rem;
        line-height: 2.8rem;
      }
    }

    ol ol {
      list-style: lower-alpha;
    }

    ol ol > li::before {
      content: initial;
    }

    #root {
      height: 100vh;
      display: flex;
      flex-direction: column;
    }

    /** Animations for buttons */

    @keyframes hover-in-transactional {
      0.00% {
        background-color: #65a01b;
        padding-top: 0;
        width: 0;
      }

      to {
        background-color: #65a01b;
        padding-top: 100rem;
        width: 100rem;
      }
    }

    @keyframes clicked-on-transactional {
      50% {
        -webkit-transform: scale(0.9);
        transform: scale(0.9);
        background-color: #65a01b !important;
        border-color: #65a01b;
      }

      to {
        -webkit-transform: scale(1);
        transform: scale(1);
        background-color: #65a01b !important;
        border-color: #65a01b;
      }
    }

    @keyframes hover-in {
      0.00% {
        background-color: rgba(0, 0, 0, 0.2);
        padding-top: 0;
        width: 0;
      }

      to {
        background-color: rgba(0, 0, 0, 0.2);
        padding-top: 100rem;
        width: 100rem;
      }
    }

    @keyframes clicked-on {
      50% {
        -webkit-transform: scale(0.9);
        transform: scale(0.9);
        background-color: rgba(0, 0, 0, 0.2) !important;
        border-color: rgba(0, 0, 0, 0.2);
      }

      to {
        -webkit-transform: scale(1);
        transform: scale(1);
        background-color: rgba(0, 0, 0, 0.2) !important;
        border-color: rgba(0, 0, 0, 0.2);
      }
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `}
`

export default GlobalStyle
